// src/pages/trading-portal/TradeExecution/AIAgentStatementPage.js
// AI Agent Statement — period account statement + per-fill transaction ledger,
// downloadable as PDF or Excel.
//
// Both downloads are generated server-side and fetched as blobs so the exported
// figures come from the same controller as the numbers on screen. Doing it in
// the browser would also have meant adding an xlsx dependency (the app ships
// jspdf but nothing that writes .xlsx).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Skeleton, Alert, Grid, Chip,
    FormControl, InputLabel, Select, MenuItem, TextField, Button, Divider,
    Snackbar, Tooltip,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem',
    textTransform: 'uppercase', letterSpacing: '0.05em',
};
const bodyCellSx = { fontFamily, fontSize: '0.85rem', color: '#334155' };

const MODULE_SLUG = 'tr-tee-ai-agent-statement';

const money = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
};

const toISODate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Quick-pick period presets. `allTime` sends ?all_time=1 instead of a range.
const buildPreset = (key) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (key) {
        case 'MTD':
            return { from: toISODate(new Date(y, m, 1)), to: toISODate(now), allTime: false };
        case 'LAST_MONTH':
            return {
                from: toISODate(new Date(y, m - 1, 1)),
                to: toISODate(new Date(y, m, 0)),   // day 0 = last day of previous month
                allTime: false,
            };
        case 'YTD':
            return { from: toISODate(new Date(y, 0, 1)), to: toISODate(now), allTime: false };
        case 'ALL':
        default:
            return { from: '', to: '', allTime: true };
    }
};

// Same conversion the Decision Log uses: the API sends offset-aware ISO, we pin
// the zone to the agent's trading timezone so the statement lines up with the
// agent's trading day rather than the reader's.
const formatInAgentTz = (iso, tzName) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    if (!tzName) return d.toLocaleString();
    try {
        return d.toLocaleString(undefined, { timeZone: tzName });
    } catch (e) {
        return d.toLocaleString();
    }
};

const StatementLine = ({ label, value, strong, negative, muted, divider }) => (
    <>
        {divider && <Divider sx={{ my: 0.75 }} />}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6 }}>
            <Typography sx={{
                fontFamily, fontSize: strong ? '0.9rem' : '0.84rem',
                fontWeight: strong ? 700 : 500,
                color: muted ? '#94a3b8' : strong ? '#1e293b' : '#64748b',
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontFamily, fontSize: strong ? '1rem' : '0.86rem',
                fontWeight: strong ? 700 : 600,
                color: negative ? '#dc2626' : strong ? '#1e293b' : '#334155',
            }}>
                {value}
            </Typography>
        </Box>
    </>
);

const AIAgentStatementPage = () => {
    const [canView, setCanView] = useState(false);
    const [permChecked, setPermChecked] = useState(false);

    const [agents, setAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState('');

    const [period, setPeriod] = useState(() => buildPreset('MTD'));
    const [activePreset, setActivePreset] = useState('MTD');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState('');
    const [snackbar, setSnackbar] = useState(null);

    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(20);

    useEffect(() => {
        (async () => {
            // Correct form: module slug + bare permission name. Sibling pages pass
            // the display name and 'can_view', which resolves 'can_can_view'.
            setCanView(await hasPermission(MODULE_SLUG, 'view'));
            setPermChecked(true);
        })();
    }, []);

    const loadAgents = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/trade-execution/ai-agents');
            const list = res.data?.data || [];
            setAgents(list);
            setSelectedAgentId((prev) => prev || (list[0]?.id ?? ''));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load agents');
        }
    }, []);

    useEffect(() => { loadAgents(); }, [loadAgents]);

    // Query string shared by the JSON load and both download endpoints, so a
    // download can never cover a different period than what is on screen.
    const periodQuery = useMemo(() => {
        const p = new URLSearchParams();
        if (period.allTime) {
            p.set('all_time', '1');
        } else {
            if (period.from) p.set('date_from', period.from);
            if (period.to) p.set('date_to', period.to);
        }
        return p.toString();
    }, [period]);

    const loadStatement = useCallback(async () => {
        if (!selectedAgentId) { setData(null); return; }
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(
                `/api/trade-execution/ai-agents/${selectedAgentId}/statement?${periodQuery}`
            );
            setData(res.data?.data || null);
            setPage(0);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load statement');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [selectedAgentId, periodQuery]);

    useEffect(() => { loadStatement(); }, [loadStatement]);

    const handleDownload = async (kind) => {
        if (!selectedAgentId) return;
        setDownloading(kind);
        try {
            const res = await axiosInstance.get(
                `/api/trade-execution/ai-agents/${selectedAgentId}/statement/export/${kind}?${periodQuery}`,
                { responseType: 'blob' }
            );
            // Prefer the server's filename; it already encodes agent + period.
            const disp = res.headers['content-disposition'] || '';
            const match = disp.match(/filename="?([^"]+)"?/);
            const fallback = `statement.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
            saveAs(res.data, match ? match[1] : fallback);
        } catch (err) {
            setSnackbar(`Failed to download ${kind === 'excel' ? 'Excel' : 'PDF'}`);
        } finally {
            setDownloading('');
        }
    };

    const applyPreset = (key) => {
        setActivePreset(key);
        setPeriod(buildPreset(key));
    };

    const onDateChange = (field) => (e) => {
        setActivePreset('');   // any manual edit leaves preset mode
        setPeriod((p) => ({ ...p, [field]: e.target.value, allTime: false }));
    };

    if (permChecked && !canView) {
        return <Alert severity="warning" sx={{ fontFamily }}>
            You do not have permission to view AI Agent Statements.
        </Alert>;
    }

    const s = data?.statement;
    const agent = data?.agent;
    const tz = agent?.trading_timezone;
    const ledger = data?.ledger || [];
    const pageRows = ledger.slice(page * perPage, page * perPage + perPage);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#1e293b' }}>
                    AI Agent Statement
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    Period account statement and per-fill transaction ledger for an AI trading agent
                </Typography>
            </Box>

            {/* Controls */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily }}>Agent</InputLabel>
                            <Select label="Agent" value={selectedAgentId} sx={{ fontFamily }}
                                onChange={(e) => setSelectedAgentId(e.target.value)}>
                                {agents.length === 0 && (
                                    <MenuItem value="" disabled sx={{ fontFamily }}>No agents found</MenuItem>
                                )}
                                {agents.map((a) => (
                                    <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>
                                        {a.name} ({a.symbol})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField size="small" fullWidth type="date" label="From" InputLabelProps={{ shrink: true }}
                            value={period.from} onChange={onDateChange('from')}
                            disabled={period.allTime} sx={{ fontFamily }} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField size="small" fullWidth type="date" label="To" InputLabelProps={{ shrink: true }}
                            value={period.to} onChange={onDateChange('to')}
                            disabled={period.allTime} sx={{ fontFamily }} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                            {[['MTD', 'MTD'], ['LAST_MONTH', 'Last month'], ['YTD', 'YTD'], ['ALL', 'All time']]
                                .map(([key, label]) => (
                                    <Chip key={key} label={label} size="small" clickable
                                        onClick={() => applyPreset(key)}
                                        variant={activePreset === key ? 'filled' : 'outlined'}
                                        sx={{
                                            fontFamily, fontSize: '0.72rem',
                                            bgcolor: activePreset === key ? `${ACCENT}22` : 'transparent',
                                            borderColor: activePreset === key ? ACCENT : '#e2e8f0',
                                            color: activePreset === key ? '#b45309' : '#64748b',
                                        }} />
                                ))}
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" startIcon={<MuiIcons.Refresh />}
                        onClick={loadStatement} disabled={loading || !selectedAgentId}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>
                        Refresh
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined" startIcon={<MuiIcons.PictureAsPdf />}
                        onClick={() => handleDownload('pdf')}
                        disabled={!!downloading || !selectedAgentId || loading}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#dc2626', color: '#dc2626' }}>
                        {downloading === 'pdf' ? 'Preparing…' : 'Download PDF'}
                    </Button>
                    <Button size="small" variant="contained" startIcon={<MuiIcons.TableView />}
                        onClick={() => handleDownload('excel')}
                        disabled={!!downloading || !selectedAgentId || loading}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
                        {downloading === 'excel' ? 'Preparing…' : 'Download Excel'}
                    </Button>
                </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ fontFamily, mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}><Skeleton variant="rectangular" height={340} sx={{ borderRadius: '12px' }} /></Grid>
                    <Grid item xs={12} md={7}><Skeleton variant="rectangular" height={340} sx={{ borderRadius: '12px' }} /></Grid>
                </Grid>
            ) : !data ? (
                <Alert severity="info" sx={{ fontFamily }}>
                    Select an agent to view its statement.
                </Alert>
            ) : (
                <Grid container spacing={2}>
                    {/* ---- Statement ---- */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                Statement
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mb: 1.5 }}>
                                {agent?.name} ({agent?.symbol}) ·{' '}
                                {data.period.all_time ? 'All time' : `${data.period.date_from} → ${data.period.date_to}`}
                                {tz ? ` · times in ${tz}` : ''}
                            </Typography>

                            <StatementLine
                                label={
                                    <Tooltip title="This is the agent's configured capital allocation, not settled broker cash — there is no funding/deposit record in the system.">
                                        <span>Opening Balance (allocation)</span>
                                    </Tooltip>
                                }
                                value={money(s.opening_balance)} />
                            <StatementLine label="Gross Buys" value={money(-s.gross_buys)} negative={s.gross_buys > 0} />
                            <StatementLine label="Gross Sells" value={money(s.gross_sells)} />
                            <StatementLine label="Commission" value={money(-s.commission)} negative={s.commission > 0} />
                            <StatementLine label="Fees" value={money(-s.fees)} negative={s.fees > 0} />
                            <StatementLine label="Net Cash Flow" value={money(s.net_cash_flow)} negative={s.net_cash_flow < 0} divider />
                            <StatementLine label="Closing Balance" value={money(s.closing_balance)} strong divider />

                            <Divider sx={{ my: 1.5 }} />
                            <StatementLine label="Realized P&L (closed trades)" value={money(s.realized_pnl)} negative={s.realized_pnl < 0} />
                            <StatementLine label="Closed Trades" value={s.closed_trades} />
                            <StatementLine label="Winning / Losing" value={`${s.winning_trades} / ${s.losing_trades}`} />
                            <StatementLine label="Win Rate" value={`${s.win_rate}%`} />
                            <StatementLine label="Best / Worst" value={`${money(s.best_trade)} / ${money(s.worst_trade)}`} />
                            <StatementLine label="Profit Factor" value={s.profit_factor} />
                            <StatementLine label="Fills (buy / sell)" value={`${s.buy_fills} / ${s.sell_fills}`} muted />

                            {/* Order-status breakdown: a statement that hides rejections is
                                misleading when submission is what is failing. */}
                            {data.order_status_counts && Object.keys(data.order_status_counts).length > 0 && (
                                <>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, mb: 0.75 }}>
                                        ALL ORDERS BY STATUS
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                        {Object.entries(data.order_status_counts).map(([st, n]) => (
                                            <Chip key={st} size="small" label={`${st}: ${n}`} variant="outlined"
                                                sx={{
                                                    fontFamily, fontSize: '0.68rem',
                                                    borderColor: st === 'FILLED' ? '#bbf7d0' : st === 'REJECTED' ? '#fecaca' : '#e2e8f0',
                                                    color: st === 'FILLED' ? '#15803d' : st === 'REJECTED' ? '#b91c1c' : '#64748b',
                                                }} />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Paper>
                    </Grid>

                    {/* ---- Ledger ---- */}
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                            <Box sx={{ p: 2, pb: 1 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                    Transaction Ledger
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                    One row per fill · {ledger.length} {ledger.length === 1 ? 'entry' : 'entries'}
                                </Typography>
                            </Box>
                            <TableContainer sx={{ maxHeight: 560 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headCellSx}>Date / Time{tz ? ` (${tz})` : ''}</TableCell>
                                            <TableCell sx={headCellSx}>Symbol</TableCell>
                                            <TableCell sx={headCellSx}>Side</TableCell>
                                            <TableCell sx={headCellSx} align="right">Qty</TableCell>
                                            <TableCell sx={headCellSx} align="right">Price</TableCell>
                                            <TableCell sx={headCellSx} align="right">Gross</TableCell>
                                            <TableCell sx={headCellSx} align="right">Comm.</TableCell>
                                            <TableCell sx={headCellSx} align="right">Fees</TableCell>
                                            <TableCell sx={headCellSx} align="right">Cash Flow</TableCell>
                                            <TableCell sx={headCellSx} align="right">Balance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pageRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ ...bodyCellSx, py: 4, color: '#94a3b8' }}>
                                                    No fills in this period.
                                                </TableCell>
                                            </TableRow>
                                        ) : pageRows.map((r) => (
                                            <TableRow key={r.fill_id} hover>
                                                <TableCell sx={bodyCellSx}>{formatInAgentTz(r.timestamp, tz)}</TableCell>
                                                <TableCell sx={bodyCellSx}>{r.symbol || '—'}</TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Chip label={r.side || '—'} size="small" variant="outlined"
                                                        sx={{
                                                            fontFamily, fontSize: '0.65rem', height: 20,
                                                            borderColor: String(r.side).startsWith('BUY') ? '#bbf7d0' : '#fecaca',
                                                            color: String(r.side).startsWith('BUY') ? '#15803d' : '#b91c1c',
                                                        }} />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{r.quantity}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{money(r.price)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{money(r.gross_value)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{money(r.commission)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{money(r.fees)}</TableCell>
                                                <TableCell sx={{ ...bodyCellSx, color: r.cash_flow < 0 ? '#dc2626' : '#15803d', fontWeight: 600 }} align="right">
                                                    {money(r.cash_flow)}
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{money(r.balance)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination component="div" count={ledger.length} page={page} rowsPerPage={perPage}
                                onPageChange={(_, p) => setPage(p)}
                                onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 20, 50, 100]} sx={{ fontFamily }} />
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
                message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} />
        </Box>
    );
};

export default AIAgentStatementPage;
